import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import MrAvvForm from "./components/MrAvvForm";
import AdminPortal from './components/AdminPortal';
import Login from './components/Login';
import CustomerDashboard from './components/CustomerDashboard';
import { AuthProvider } from './context/AuthContext';
import RoleBasedRoute from './components/RoleBasedRoute';
import ModelReportDatabase from './components/ModelReportDatabase';
import DownloadReportFiles from './components/DownloadReportFiles';
import SendMarketingEmail from './components/SendMarketingEmail';
import AddNewCustomerModelReport from './components/AddNewCustomerModelReport';
import PrivacyPolicy from './components/PrivacyPolicy';
import Cart from './components/Cart';
import OrderHistory from './components/OrderHistory';
import SevsExpiryManagement from './components/sevsExpiryManagement/SevsExpiryManagement';
import ArchivedSevs from './components/sevsExpiryManagement/ArchivedSevs';
import ArchivedOrders from './components/archive/ArchivedOrders';
import CustomerArchivedOrders from './components/archive/CustomerArchivedOrders';
import ViewAnalysis from './components/analysis/ViewAnalysis';
import Section17PhotosUpload from './components/Section17PhotosUpload';
import VariationHistory from './components/VariationHistory';
import UniqueIdVerification from './components/UniqueIdVerification';
import PriceManagement from './components/PriceManagement';
import FeedbackForm from './components/FeedbackForm';

// Stripe returns to /payment-success; back to the cart, which finalises the order from those params.
const PaymentSuccessRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/cart${search}`} replace />;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'owner']}>
                <AdminPortal />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/admin/price-management"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'owner']}>
                <PriceManagement />
              </RoleBasedRoute>
            }
          />
          {/* Same component as the customer route below, but mode="admin"
              unlocks editing and ADMIN-only columns */}
          <Route
            path="/admin/model-reports"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'owner']}>
                <ModelReportDatabase mode="admin" />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/admin/model-reports/:id"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'owner']}>
                <ModelReportDatabase mode="admin" />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/send-marketing-email"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'owner']}>
                <SendMarketingEmail />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/add-new-customer-model-reports"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'owner']}>
                <AddNewCustomerModelReport />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/view-analysis"
            element={
              <RoleBasedRoute allowedRoles={['owner']}>
                <ViewAnalysis />
              </RoleBasedRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <RoleBasedRoute allowedRoles={['customer', 'admin', 'owner']}>
                <CustomerDashboard />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/order-history"
            element={
              <RoleBasedRoute allowedRoles={['customer', 'admin', 'owner']}>
                <OrderHistory />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/customer-archived-orders"
            element={
              <RoleBasedRoute allowedRoles={['customer', 'admin', 'owner']}>
                <CustomerArchivedOrders />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/feedback-form"
            element={
              <RoleBasedRoute allowedRoles={['customer', 'admin', 'owner']}>
                <FeedbackForm />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/cart"
            element={
              <RoleBasedRoute allowedRoles={['customer', 'admin', 'owner']}>
                <Cart />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/payment-success"
            element={
              <RoleBasedRoute allowedRoles={['customer', 'admin', 'owner']}>
                <PaymentSuccessRedirect />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/new-order"
            element={
              <RoleBasedRoute allowedRoles={['customer', 'admin', 'owner']}>
                <MrAvvForm />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/verify-vin"
            element={
              <RoleBasedRoute allowedRoles={['customer', 'admin', 'owner']}>
                <UniqueIdVerification />
              </RoleBasedRoute>
            }
          />


          {/* Redirect root to dashboard or login */}
          <Route
            path="/"
            element={
              <RoleBasedRoute allowedRoles={['customer', 'admin', 'owner']}>
                <Navigate to="/dashboard" replace />
              </RoleBasedRoute>
            }
          />

          {/* Catch all other routes and redirect to dashboard */}
          <Route
            path="*"
            element={
              <RoleBasedRoute allowedRoles={['customer', 'admin', 'owner']}>
                <Navigate to="/dashboard" replace />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/modelreportdatabase/:id"
            element={
              <RoleBasedRoute allowedRoles={['customer', 'admin', 'owner']}>
                <ModelReportDatabase />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/modelreportdatabase"
            element={
              <RoleBasedRoute allowedRoles={['customer', 'admin', 'owner']}>
                <ModelReportDatabase />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/download-report-files/:id?"
            element={
              <RoleBasedRoute allowedRoles={['customer', 'admin', 'owner']}>
                <DownloadReportFiles />
              </RoleBasedRoute>
            }
          />
          <Route path="/sevs-expiry" element={<RoleBasedRoute allowedRoles={['admin', 'owner']}><SevsExpiryManagement /></RoleBasedRoute>} />
          <Route path="/archived-sevs" element={<RoleBasedRoute allowedRoles={['admin', 'owner']}><ArchivedSevs /></RoleBasedRoute>} />
          <Route path="/archived-orders" element={<RoleBasedRoute allowedRoles={['admin', 'owner']}><ArchivedOrders /></RoleBasedRoute>} />
          <Route path="/section-17-management" element={<RoleBasedRoute allowedRoles={['admin', 'owner']}><Section17PhotosUpload /></RoleBasedRoute>} />


          <Route
            path="/admin/variation-history"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'owner']}>
                <VariationHistory />
              </RoleBasedRoute>
            }
          />

        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
